import { useEffect, useState } from "react";

import {
  canBeNarrowed,
  clauseAsSaid,
  whereAsked,
  whereAsks,
  type ReportSource,
  type WhereGroup,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import { ReportsViewWidgetWhereEditor } from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidgetWhereEditor";
import { Popper } from "@/components/atoms/Popper";
import { cn } from "@/components/ui/utils";

/**
 * Which rows a widget is asked of, said after the question it asks.
 *
 * "Total amount of transactions per contact" is every transaction; a clause
 * puts a "where" after it. Comparisons group and nest - all of these, or any
 * of those - because "at Shell, and either over a hundred or fuel" is a
 * different question from "at Shell and over a hundred, or fuel", and a chart
 * can only be drawn from one of them. It is written with the editor rules are
 * written with, which already knows how to nest.
 */
export function ReportsViewWidgetWhere({
  source,
  where,
  onChange,
}: {
  source: ReportSource | undefined;
  where: WhereGroup | null | undefined;
  onChange: (where: WhereGroup | null) => void;
}) {
  const isOpenState = useState(false);
  const [isOpen] = isOpenState;
  // A clause being written is not yet a clause: a row with nothing chosen in
  // it narrows nothing, and a widget is not asked about it. It has to stay on
  // screen all the same, or adding one does nothing you can see - which is
  // what happens when the editor is handed back only what has been stored.
  const [draft, setDraft] = useState<WhereGroup | null>(null);
  useEffect(() => {
    if (!isOpen) setDraft(null);
  }, [isOpen]);
  const said = clauseAsSaid(where, source);
  const isAsking = whereAsks(where);

  if (!canBeNarrowed(source)) return null;

  const anchor = (
    <span
      data-testid="widget-where"
      // Its own press, like every other word of the sentence: the header is
      // the card's drag handle, and this is not a place to take hold of it.
      onPointerDown={(event) => event.stopPropagation()}
      className={cn(
        "inline cursor-pointer rounded-sm pl-0.5 pr-0.5 -ml-0.5 -mr-0.5",
        isAsking ? "text-foreground" : "text-muted-foreground/80",
        isAsking ? "[font-weight:600]" : "font-medium",
        "underline [text-decoration-thickness:initial] decoration-dotted decoration-border underline-offset-[3px] hover:bg-muted",
      )}
    >
      {isAsking ? `where ${said}` : "anywhere"}
    </span>
  );

  return (
    <Popper
      anchor={anchor}
      isOpenState={isOpenState}
      placement="bottom-start"
      // Opened, a clause is read before it is changed - and a value under the
      // cursor shows its digits rather than its dollars, so focusing the
      // first one would open the clause half undressed.
      autoFocus={false}
    >
      <div className="p-3 min-w-105 max-w-140">
        <ReportsViewWidgetWhereEditor
          source={source}
          group={draft ?? where ?? { connector: "all", terms: [] }}
          onChange={(group) => {
            setDraft(group);
            // Only what is finished reaches the widget: a comparison with
            // nothing to compare against would narrow it to nothing at all
            // while the rest of the row was still being written.
            const next = whereAsked(group);
            onChange(whereAsks(next) ? next : null);
          }}
        />
      </div>
    </Popper>
  );
}
